import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Briefcase,
  CaretLeft,
  CarProfile,
  Crosshair,
  CreditCard,
  Headset,
  HouseLine,
  IdentificationCard,
  MapPin,
  PaperPlaneTilt,
  Robot,
  ShieldCheck,
  SlidersHorizontal,
  SteeringWheel,
  Trash,
  User,
  UsersThree,
} from "@phosphor-icons/react";
import { api, CITY_MAP_ZOOM, DRC_CENTER, DRC_MAP_ZOOM, formatApiError, formatCDF } from "../../lib/api";
import { reverseGeocode } from "../../lib/geo";
import { getPreciseLocation } from "../../lib/preciseLocation";
import AddressSearch from "../../components/AddressSearch";
import MapView from "../../components/MapView";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import { Badge, Button, Card, Input, Label, StatusBadge } from "../../components/ui-kit";

const SECTIONS = {
  profile: {
    title: "Profil",
    icon: User,
    fields: [
      ["name", "Nom complet", "text"],
      ["phone", "Telephone", "tel"],
      ["emergency_contact", "Contact d'urgence", "tel"],
      ["preferred_name", "Nom affiche", "text"],
    ],
  },
  payment: {
    title: "Paiements",
    icon: CreditCard,
    fields: [
      ["default_method", "Methode par defaut", "select", ["cash", "mpesa", "airtel", "orange"]],
      ["mobile_money_phone", "Numero mobile money", "tel"],
      ["billing_name", "Nom facturation", "text"],
      ["billing_address", "Adresse facturation", "text"],
    ],
  },
  support: {
    title: "Support",
    icon: Headset,
    fields: [
      ["subject", "Sujet", "text"],
      ["ride_id", "ID course liee", "text"],
      ["priority", "Priorite", "select", ["normal", "urgent", "security"]],
      ["message", "Message", "textarea"],
    ],
  },
  safety: {
    title: "Safety",
    icon: ShieldCheck,
    fields: [
      ["trusted_contact_name", "Contact de confiance", "text"],
      ["trusted_contact_phone", "Telephone du contact", "tel"],
      ["ride_pin_required", "PIN course obligatoire", "checkbox"],
      ["share_trip_enabled", "Partager trajet automatiquement", "checkbox"],
      ["medical_note", "Note securite/medicale", "textarea"],
    ],
  },
  "saved-places": {
    title: "Saved places",
    icon: MapPin,
    fields: [
      ["home_address", "Maison", "text"],
      ["work_address", "Travail", "text"],
      ["favorite_place_1", "Lieu favori 1", "text"],
      ["favorite_place_2", "Lieu favori 2", "text"],
    ],
  },
  settings: {
    title: "Settings",
    icon: SlidersHorizontal,
    fields: [
      ["language", "Langue", "select", ["fr", "ln", "sw"]],
      ["theme", "Theme", "select", ["dark", "light"]],
      ["push_notifications", "Notifications push", "checkbox"],
      ["email_notifications", "Notifications email", "checkbox"],
      ["whatsapp_notifications", "Notifications WhatsApp", "checkbox"],
      ["receipt_auto", "Recu automatique", "checkbox"],
    ],
  },
  family: {
    title: "Family profile",
    icon: UsersThree,
    fields: [
      ["profile_name", "Nom du profil famille", "text"],
      ["member_1", "Membre 1", "text"],
      ["member_1_phone", "Telephone membre 1", "tel"],
      ["member_2", "Membre 2", "text"],
      ["monthly_limit", "Limite mensuelle CDF", "number"],
      ["track_family_rides", "Suivre les courses famille", "checkbox"],
    ],
  },
  work: {
    title: "Work profile",
    icon: Briefcase,
    fields: [
      ["company_name", "Entreprise", "text"],
      ["work_email", "Email professionnel", "email"],
      ["tax_number", "Numero fiscal/RCCM", "text"],
      ["expense_code", "Code suivi interne", "text"],
      ["monthly_limit", "Limite mensuelle CDF", "number"],
      ["auto_receipts", "Recus automatiques", "checkbox"],
    ],
  },
  earn: {
    title: "Earn with Nexora",
    icon: SteeringWheel,
    fields: [
      ["interest_type", "Type", "select", ["driver", "fleet_owner", "partner"]],
      ["city_id", "Ville", "city"],
      ["vehicle_available", "Vehicule disponible", "checkbox"],
      ["vehicle_plate", "Plaque", "text"],
      ["notes", "Notes", "textarea"],
    ],
  },
  rides: {
    title: "Rides",
    icon: CarProfile,
    fields: [
      ["default_vehicle_type", "Vehicule prefere", "select", ["moto", "economy", "comfort", "premium", "xl"]],
      ["quiet_ride", "Trajet silencieux", "checkbox"],
      ["air_conditioning", "Climatisation", "checkbox"],
      ["help_luggage", "Aide bagages", "checkbox"],
    ],
  },
  home: {
    title: "Home",
    icon: HouseLine,
    fields: [
      ["default_pickup", "Depart par defaut", "text"],
      ["default_destination", "Destination favorite", "text"],
      ["map_hint", "Instruction carte", "textarea"],
    ],
  },
  identity: {
    title: "Verify identity",
    icon: IdentificationCard,
    fields: [],
  },
};

const SAVED_PLACE_FIELDS = [
  { key: "home_address", label: "Maison", hint: "Maison, parcelle ou residence exacte" },
  { key: "work_address", label: "Travail", hint: "Bureau, magasin ou lieu de travail" },
  { key: "favorite_place_1", label: "Lieu favori 1", hint: "Ecole, hopital, eglise, famille..." },
  { key: "favorite_place_2", label: "Lieu favori 2", hint: "Deuxieme lieu exact a reutiliser" },
];

const normalizeSavedPlace = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    return { label: value, short: value, address_text: value };
  }
  return value;
};

const savedPlaceLabel = (value) => {
  const place = normalizeSavedPlace(value);
  if (!place) return "";
  return place.short || place.label || place.address_text || place.address?.road || "";
};

const makeSavedPlace = (place, source = "search") => ({
  lat: Number(place.lat),
  lng: Number(place.lng),
  label: place.label || place.display_name || place.short || `${Number(place.lat).toFixed(5)}, ${Number(place.lng).toFixed(5)}`,
  short: place.short || place.name || place.label?.split(",")?.[0] || "Lieu selectionne",
  address_text: typeof place.address === "string" ? place.address : place.label || "",
  address: typeof place.address === "object" ? place.address : null,
  type: place.type || null,
  category: place.category || null,
  source,
  saved_at: new Date().toISOString(),
});

function SavedPlacesEditor({ form, onChange, cityCenter }) {
  const [activeKey, setActiveKey] = useState("home_address");
  const [locatingKey, setLocatingKey] = useState("");
  const activePlace = normalizeSavedPlace(form[activeKey]);
  const hasActiveCoords = Number.isFinite(Number(activePlace?.lat)) && Number.isFinite(Number(activePlace?.lng));
  const mapCenter = hasActiveCoords
    ? { lat: Number(activePlace.lat), lng: Number(activePlace.lng) }
    : cityCenter;

  const setPlace = (key, place, source = "search") => {
    const saved = makeSavedPlace(place, source);
    if (!Number.isFinite(saved.lat) || !Number.isFinite(saved.lng)) {
      toast.error("Selectionnez un lieu exact sur la carte");
      return;
    }
    onChange(key, saved);
    setActiveKey(key);
  };

  const handleCurrentPosition = async (key) => {
    setLocatingKey(key);
    try {
      const loc = await getPreciseLocation();
      const reverse = await reverseGeocode(loc.lat, loc.lng);
      setPlace(
        key,
        {
          ...loc,
          ...(reverse || {}),
          label: reverse?.label || `Position actuelle (${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)})`,
          short: reverse?.short || "Position actuelle",
        },
        "gps"
      );
      toast.success("Position actuelle ajoutee");
    } catch (e) {
      toast.error(e?.message || "Impossible de recuperer la position");
    } finally {
      setLocatingKey("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/20">
        <MapView
          center={mapCenter}
          pickup={hasActiveCoords ? mapCenter : null}
          zoom={hasActiveCoords ? CITY_MAP_ZOOM + 2 : DRC_MAP_ZOOM}
          height="190px"
          minHeight={190}
          className="h-[190px]"
        />
      </div>

      <div className="rounded-2xl border border-primary/25 bg-primary/10 p-3 text-xs leading-5 text-muted-foreground">
        Choisis un resultat exact depuis la recherche ou utilise ta position actuelle. Les coordonnees sont sauvegardees avec le nom du lieu pour eviter les adresses au hasard.
      </div>

      <div className="grid gap-3">
        {SAVED_PLACE_FIELDS.map((item) => {
          const place = normalizeSavedPlace(form[item.key]);
          const hasCoords = Number.isFinite(Number(place?.lat)) && Number.isFinite(Number(place?.lng));
          return (
            <div
              key={item.key}
              className={`rounded-3xl border p-3 transition ${
                activeKey === item.key ? "border-primary/60 bg-primary/10" : "border-white/10 bg-white/[0.04]"
              }`}
              onFocus={() => setActiveKey(item.key)}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label>{item.label}</Label>
                  <div className="mt-1 text-[11px] text-muted-foreground">{item.hint}</div>
                </div>
                {hasCoords && <Badge tone="success">Carte OK</Badge>}
              </div>

              <AddressSearch
                cityCenter={cityCenter}
                valueLabel={savedPlaceLabel(place)}
                placeholder={`Chercher ${item.label.toLowerCase()} sur la carte...`}
                onSelect={(selected) => setPlace(item.key, selected, "search")}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCurrentPosition(item.key)}
                  disabled={locatingKey === item.key}
                  className="gap-2"
                >
                  <Crosshair size={15} weight="bold" />
                  {locatingKey === item.key ? "Localisation..." : "Position actuelle"}
                </Button>
                {place && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange(item.key, null)}
                    className="gap-2 text-danger hover:text-danger"
                  >
                    <Trash size={15} weight="bold" />
                    Effacer
                  </Button>
                )}
              </div>

              {place && (
                <div className="mt-3 rounded-2xl bg-black/20 p-3 text-xs">
                  <div className="line-clamp-2 font-bold">{place.label || place.address_text || place.short}</div>
                  {hasCoords ? (
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {Number(place.lat).toFixed(6)}, {Number(place.lng).toFixed(6)}
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] text-warning">
                      Ancien lieu sans coordonnees: recherche-le a nouveau pour le rendre exact sur la carte.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ field, value, onChange, cities = [] }) {
  const [key, label, type, options] = field;
  if (type === "checkbox") {
    return (
      <label className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 p-3">
        <span className="text-sm font-bold">{label}</span>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(key, e.target.checked)}
          className="h-5 w-5 accent-[hsl(var(--primary))]"
        />
      </label>
    );
  }
  return (
    <div>
      <Label>{label}</Label>
      {type === "city" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(key, e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="">Choisir une ville</option>
          {cities.map((city) => (
            <option key={city.id} value={city.id}>
              {city.name} - {city.province}
            </option>
          ))}
        </select>
      ) : type === "select" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(key, e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="">Choisir</option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(key, e.target.value)}
          rows={4}
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      ) : (
        <Input type={type} value={value || ""} onChange={(e) => onChange(key, e.target.value)} />
      )}
    </div>
  );
}

export default function AccountOptionPage() {
  const { section } = useParams();
  const cfg = SECTIONS[section] || SECTIONS.profile;
  const Icon = cfg.icon;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({});
  const [tickets, setTickets] = useState([]);
  const [cities, setCities] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiMessages, setAiMessages] = useState([
    {
      role: "assistant",
      text: "Bonjour, qu'est-ce que je peux faire pour vous ? Je peux aider pour un compte, une course, une livraison, un paiement, un restaurant, un supermarche ou un bug.",
    },
  ]);
  const [aiLoading, setAiLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);

  const cityCenter = useMemo(() => {
    const cityId =
      account?.user?.city_id ||
      account?.profile?.customer_profile?.city_id ||
      account?.profile?.driver_profile?.city_id ||
      form?.city_id;
    const city = cities.find((item) => item.id === cityId);
    if (!city) return DRC_CENTER;
    return { lat: Number(city.lat), lng: Number(city.lng) };
  }, [account, cities, form?.city_id]);

  const initialForm = useMemo(() => {
    const saved = account?.options?.[section] || {};
    const fromUser = section === "profile" ? {
      name: account?.user?.name,
      phone: account?.user?.phone,
    } : {};
    return { ...fromUser, ...saved };
  }, [account, section]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/account/options"),
      api.get("/cities"),
      section === "support" ? api.get("/disputes/me") : Promise.resolve({ data: [] }),
    ])
      .then(([accountRes, cityRes, ticketRes]) => {
        setAccount(accountRes.data);
        setCities(Array.isArray(cityRes.data) ? cityRes.data : []);
        setTickets(Array.isArray(ticketRes.data) ? ticketRes.data : []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [section]);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const askSupportAi = async () => {
    const question = (aiQuestion || form.message || form.subject || "").trim();
    if (question.length < 2) {
      toast.error("Ecrivez votre question");
      return;
    }
    setAiMessages((current) => [...current, { role: "user", text: question }]);
    setAiQuestion("");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/support", {
        question,
        create_ticket: false,
        ride_id: form.ride_id || null,
      });
      setAiMessages((current) => [
        ...current,
        {
          role: "assistant",
          text: data.answer,
          ticketRecommended: data.ticket_recommended,
          ticketReason: data.ticket_reason,
          ticketCreated: data.ticket_created,
          ticketId: data.ticket?.id,
        },
      ]);
      if (data.ticket_created) {
        const ticketsRes = await api.get("/disputes/me");
        setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
        toast.success("Ticket support cree");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setAiMessages((current) => [
        ...current,
        { role: "assistant", text: "Je n'arrive pas a repondre maintenant. Reessayez dans un instant ou creez un ticket support manuel plus bas." },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const changePassword = async () => {
    if (!passwordForm.new_password || passwordForm.new_password.length < 8) {
      toast.error("Mot de passe: 8 caracteres minimum");
      return;
    }
    setPasswordSaving(true);
    try {
      await api.post("/auth/change-password", passwordForm);
      setPasswordForm({ current_password: "", new_password: "" });
      toast.success("Mot de passe mis a jour");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPasswordSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (section === "support") {
        await api.post("/disputes", {
          ride_id: form.ride_id || null,
          category: form.priority === "security" ? "safety" : "other",
          title: form.subject || "Demande support",
          message: form.message || "Demande support",
          requested_refund_amount: null,
        });
        const { data } = await api.get("/disputes/me");
        setTickets(Array.isArray(data) ? data : []);
        setForm({});
        toast.success("Ticket envoye au support");
        return;
      }
      const { data } = await api.put(`/account/options/${section}`, { data: form });
      setAccount((current) => ({
        ...current,
        profile: data.profile,
        options: data.profile?.account_options || {},
      }));
      toast.success("Enregistre");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (section === "identity") {
    return null;
  }

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title={cfg.title} />
      <div className="space-y-4 px-4 pt-2">
        <Link to="/app/profile" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
          <CaretLeft size={14} weight="bold" /> Compte
        </Link>

        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Icon size={25} weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-2xl font-bold">{cfg.title}</div>
              <div className="text-xs text-muted-foreground">
                {loading ? "Chargement..." : "Modifie et sauvegarde tes informations"}
              </div>
            </div>
            {account?.identity?.status && <Badge tone="primary">{account.identity.status}</Badge>}
          </div>
        </Card>

        {section === "rides" && (
          <Card className="p-0">
            <div className="border-b border-white/10 p-4 font-heading text-lg font-bold">Mes courses</div>
            <div className="divide-y divide-white/5">
              {account?.rides?.length === 0 && <div className="p-5 text-center text-xs text-muted-foreground">Aucune course</div>}
              {account?.rides?.slice(0, 10).map((ride) => (
                <Link key={ride.id} to={`/app/ride/${ride.id}`} className="flex items-center justify-between gap-3 p-3 hover:bg-white/5">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-bold">#{ride.id.slice(0, 6).toUpperCase()} - {ride.vehicle_type}</div>
                    <div className="text-[10px] text-muted-foreground">{ride.pickup?.address || "Depart"} -> {ride.destination?.address || "Destination"}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <StatusBadge status={ride.status} />
                    <div className="mt-1 font-mono text-xs font-bold">{formatCDF(ride.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {section === "support" && (
          <Card className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Robot size={20} weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg font-bold">Assistant IA support</div>
                <div className="text-xs text-muted-foreground">L'IA repond d'abord. Si une personne doit intervenir, elle ouvre un ticket automatiquement.</div>
              </div>
            </div>
            <div className="max-h-[360px] space-y-2 overflow-y-auto rounded-2xl border border-white/10 bg-black/20 p-3">
              {aiMessages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`max-w-[92%] rounded-2xl p-3 text-sm leading-relaxed ${
                    message.role === "user"
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "mr-auto border border-primary/20 bg-primary/10"
                  }`}
                >
                  {message.text}
                  {message.ticketReason && (
                    <div className={`mt-3 rounded-xl p-2 text-xs font-bold ${message.ticketRecommended ? "bg-amber-500/15 text-amber-200" : "bg-emerald-500/15 text-emerald-200"}`}>
                      {message.ticketRecommended ? "Analyse IA: action humaine recommandee. " : "Analyse IA: resolution directe possible. "}
                      {message.ticketReason}
                    </div>
                  )}
                  {message.ticketCreated && (
                    <div className="mt-2 text-xs font-bold text-primary">
                      Ticket #{message.ticketId?.slice(0, 8)?.toUpperCase()} envoye au support.
                    </div>
                  )}
                </div>
              ))}
              {aiLoading && (
                <div className="mr-auto max-w-[92%] rounded-2xl border border-primary/20 bg-primary/10 p-3 text-sm">
                  L'IA analyse votre demande...
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <textarea
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                rows={2}
                className="min-h-[56px] flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm outline-none focus:border-primary"
                placeholder="Ecrire votre question..."
                data-testid="support-ai-question"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    askSupportAi();
                  }
                }}
              />
              <Button type="button" onClick={askSupportAi} disabled={aiLoading} className="self-stretch px-4">
                <PaperPlaneTilt size={16} weight="bold" />
                Envoyer
              </Button>
            </div>
          </Card>
        )}

        {section === "safety" && (
          <Card className="space-y-3 p-4">
            <div className="font-heading text-lg font-bold">Mot de passe</div>
            <div className="text-xs text-muted-foreground">
              Change ton mot de passe. Les appareils deja connectes seront revoques.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Mot de passe actuel</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm((current) => ({ ...current, current_password: e.target.value }))}
                  placeholder="Mot de passe actuel"
                  data-testid="current-password"
                />
              </div>
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm((current) => ({ ...current, new_password: e.target.value }))}
                  placeholder="Nouveau mot de passe solide"
                  data-testid="new-password"
                />
              </div>
            </div>
            <Button type="button" onClick={changePassword} disabled={passwordSaving} className="w-full" data-testid="change-password">
              {passwordSaving ? "Mise a jour..." : "Changer le mot de passe"}
            </Button>
          </Card>
        )}

        {section === "support" && (
          <Card className="p-0">
            <div className="border-b border-white/10 p-4 font-heading text-lg font-bold">Mes tickets support</div>
            <div className="divide-y divide-white/5">
              {tickets.length === 0 && <div className="p-5 text-center text-xs text-muted-foreground">Aucun ticket</div>}
              {tickets.slice(0, 10).map((ticket) => (
                <div key={ticket.id} className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="break-words text-sm font-bold">{ticket.title}</div>
                      <div className="mt-1 break-words text-xs text-muted-foreground">{ticket.message}</div>
                      {ticket.admin_note && <div className="mt-2 rounded-xl bg-white/5 p-2 text-xs text-primary">{ticket.admin_note}</div>}
                    </div>
                    <Badge tone={ticket.status === "OPEN" ? "warning" : ticket.status === "RESOLVED" ? "success" : "primary"}>{ticket.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="space-y-4 p-4">
          {section === "saved-places" ? (
            <SavedPlacesEditor form={form} onChange={change} cityCenter={cityCenter} />
          ) : (
            cfg.fields.map((field) => (
              <Field key={field[0]} field={field} value={form[field[0]]} onChange={change} cities={cities} />
            ))
          )}
          <Button onClick={save} disabled={saving || loading} className="w-full" data-testid={`save-${section}`}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </Card>
      </div>
      <BottomNav role="customer" />
    </div>
  );
}
